// File: frontend/src/components/Navbar.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSchool } from '../context/SchoolContext';
import api from '../utils/axios';

const Navbar = ({ sidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const { school } = useSchool();
  const navigate = useNavigate();

  // Dynamic Academic Year initial state
  const defaultYear = school?.session_year
    ? (school.session_year.includes('/') ? school.session_year : `${school.session_year} / ${parseInt(school.session_year) + 1}`)
    : '2025 / 2026';

  const [academicYear, setAcademicYear] = useState(defaultYear);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Global Quick Search States
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ students: [], staff: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Sync academic year if school context loads late
  useEffect(() => {
    if (school?.session_year) {
      const formatted = school.session_year.includes('/') 
        ? school.session_year 
        : `${school.session_year} / ${parseInt(school.session_year) + 1}`;
      setAcademicYear(formatted);
    }
  }, [school?.session_year]);

  // Global Cmd+K / Ctrl+K keyboard shortcut to focus search input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close search dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Execute live search across Students & Staff
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults({ students: [], staff: [] });
      setIsSearching(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearching(true);
        const [studentRes, staffRes] = await Promise.all([
          api.get(`/api/v1/students?search=${encodeURIComponent(searchQuery.trim())}&limit=5`).catch(() => ({ data: null })),
          api.get(`/api/v1/staff?search=${encodeURIComponent(searchQuery.trim())}&limit=5`).catch(() => ({ data: null }))
        ]);

        let students = [];
        if (studentRes.data?.success) {
          students = Array.isArray(studentRes.data.data) ? studentRes.data.data : (studentRes.data.students || []);
        }

        let staff = [];
        if (staffRes.data?.success) {
          staff = Array.isArray(staffRes.data.data) ? staffRes.data.data : (staffRes.data.staff || []);
        }

        setSearchResults({ students, staff });
        setShowSearchDropdown(true);
      } catch (err) {
        console.error('Global search error:', err);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header
      className={`sticky top-0 z-30 h-16 bg-white border-b border-purple-100 transition-all duration-300 flex items-center justify-between px-4 sm:px-6 shadow-sm print:hidden ${
        sidebarCollapsed ? 'ml-20' : 'ml-64'
      }`}
    >
      {/* Left Search Bar */}
      <div ref={searchContainerRef} className="flex items-center space-x-3 flex-1 max-w-md relative">
        <div className="relative w-full">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSearchDropdown(true);
            }}
            onFocus={() => {
              if (searchQuery.trim().length >= 2) setShowSearchDropdown(true);
            }}
            placeholder="Search students, staff, roll no..."
            className="w-full bg-purple-50/50 border border-purple-100 rounded-xl pl-9 pr-12 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition"
          />
          <svg className="w-4 h-4 text-purple-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="absolute right-3 top-2 text-[10px] font-mono text-purple-400 bg-white px-1.5 py-0.5 rounded border border-purple-100">
            ⌘K
          </span>
        </div>

        {/* Floating Quick Search Results Modal */}
        {showSearchDropdown && searchQuery.trim().length >= 2 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-purple-100 rounded-2xl shadow-2xl p-3 z-50 max-h-96 overflow-y-auto space-y-3">
            {isSearching ? (
              <div className="p-4 text-center text-xs text-purple-600 font-semibold animate-pulse">
                🔍 Searching directory...
              </div>
            ) : (
              <>
                {/* Students Match Category */}
                <div>
                  <p className="text-[10px] font-extrabold uppercase text-purple-500 tracking-wider mb-1.5 px-2">
                    🎓 Students ({searchResults.students.length})
                  </p>
                  {searchResults.students.length > 0 ? (
                    <div className="space-y-1">
                      {searchResults.students.map((student) => (
                        <div
                          key={student.id}
                          onClick={() => {
                            setShowSearchDropdown(false);
                            setSearchQuery('');
                            navigate(`/students/${student.id}`);
                          }}
                          className="p-2 rounded-xl hover:bg-purple-50 cursor-pointer flex items-center justify-between transition border border-transparent hover:border-purple-100"
                        >
                          <div>
                            <p className="text-xs font-bold text-slate-800">{student.name || student.student_name}</p>
                            <p className="text-[10px] text-slate-500">Roll #: {student.roll_no || student.student_id || 'N/A'} • Class: {student.class_name || student.class_id || 'N/A'}</p>
                          </div>
                          <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">View</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic px-2">No matching students found.</p>
                  )}
                </div>

                {/* Staff Match Category */}
                <div className="border-t border-purple-50 pt-2">
                  <p className="text-[10px] font-extrabold uppercase text-purple-500 tracking-wider mb-1.5 px-2">
                    👥 Staff & Teachers ({searchResults.staff.length})
                  </p>
                  {searchResults.staff.length > 0 ? (
                    <div className="space-y-1">
                      {searchResults.staff.map((member) => (
                        <div
                          key={member.id}
                          onClick={() => {
                            setShowSearchDropdown(false);
                            setSearchQuery('');
                            navigate(`/staff/${member.id}`);
                          }}
                          className="p-2 rounded-xl hover:bg-purple-50 cursor-pointer flex items-center justify-between transition border border-transparent hover:border-purple-100"
                        >
                          <div>
                            <p className="text-xs font-bold text-slate-800">{member.name || member.full_name}</p>
                            <p className="text-[10px] text-slate-500">{member.designation || member.role || 'Staff'} • Ph: {member.phone || 'N/A'}</p>
                          </div>
                          <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">View</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic px-2">No matching staff found.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Right Header Actions & Profile */}
      <div className="flex items-center space-x-3">
        {/* Dynamic Academic Year Selector */}
        <div className="hidden md:flex items-center space-x-1.5 bg-purple-50/70 px-3 py-1.5 rounded-xl border border-purple-100 text-xs font-semibold text-purple-900">
          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>Academic Year:</span>
          <select
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            className="bg-transparent font-bold text-purple-700 focus:outline-none cursor-pointer"
          >
            <option value="2025 / 2026">2025 / 2026</option>
            <option value="2026 / 2027">2026 / 2027</option>
            <option value="2024 / 2025">2024 / 2025</option>
          </select>
        </div>

        {/* Quick Action Buttons */}
        <div className="hidden lg:flex items-center space-x-2">
          <button
            onClick={() => navigate('/students/new')}
            style={{ backgroundColor: school?.button_color || 'var(--button-color)' }}
            className="px-3.5 py-1.5 text-white text-xs font-semibold rounded-xl shadow-purple-glow transition hover:brightness-110 flex items-center space-x-1"
          >
            <span>+</span>
            <span>Add Student</span>
          </button>

          <button
            onClick={() => navigate('/fees')}
            className="px-3.5 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold rounded-xl border border-purple-200 transition"
          >
            Collect Fees
          </button>
        </div>

        {/* Notifications Icon */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="p-2 text-slate-500 hover:text-purple-700 hover:bg-purple-50 rounded-xl transition relative"
            title="Notifications"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span className="w-2 h-2 rounded-full bg-red-500 absolute top-1.5 right-1.5 ring-2 ring-white animate-pulse"></span>
          </button>

          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white border border-purple-100 rounded-2xl shadow-xl p-4 z-50 space-y-3">
              <div className="flex justify-between items-center border-b border-purple-100 pb-2">
                <h4 className="font-bold text-xs text-purple-950">System Notifications</h4>
                <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">2 New</span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="p-2.5 bg-purple-50/60 rounded-xl border border-purple-100">
                  <p className="font-bold text-slate-800">Fee Payment Received</p>
                  <p className="text-slate-500 text-[11px]">Fahed III-C paid Terms 1 tuition fee.</p>
                </div>
                <div className="p-2.5 bg-purple-50/60 rounded-xl border border-purple-100">
                  <p className="font-bold text-slate-800">Leave Application</p>
                  <p className="text-slate-500 text-[11px]">James (Physics Teacher) applied for emergency leave.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* User Profile Avatar Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-2 p-1.5 rounded-xl hover:bg-purple-50 transition border border-transparent hover:border-purple-100"
          >
            <div className="w-8 h-8 rounded-xl bg-purple-700 text-white font-bold flex items-center justify-center text-xs shadow-sm">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <div className="hidden sm:block text-left leading-tight">
              <p className="text-xs font-bold text-slate-800">{user?.name || 'Account'}</p>
              <p className="text-[10px] text-purple-600 font-semibold capitalize">{user?.role || 'User'}</p>
            </div>
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white border border-purple-100 rounded-2xl shadow-xl py-2 z-50 text-xs">
              <Link
                to="/profile"
                onClick={() => setShowUserMenu(false)}
                className="px-4 py-2 text-slate-700 hover:bg-purple-50 hover:text-purple-700 flex items-center space-x-2 font-medium"
              >
                <span>👤</span>
                <span>My Profile</span>
              </Link>
              {['super_admin', 'admin'].includes(user?.role?.toLowerCase()) && (
                <Link
                  to="/branding"
                  onClick={() => setShowUserMenu(false)}
                  className="px-4 py-2 text-purple-700 font-bold hover:bg-purple-50 flex items-center space-x-2"
                >
                  <span>⚙️</span>
                  <span>Branding Settings</span>
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 flex items-center space-x-2 font-medium"
              >
                <span>🚪</span>
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
